<script lang="ts">
import { computed, defineComponent, h, ref, unref } from 'vue';

import { CdsTransitionSlideX } from '@components/transitions';
import { classesStates, classNames } from '@components/utils';

import {
  makeDisabledProps,
  makeFocusProps,
  makeInputProps,
  makeReadonlyProps,
  makeThemeProps,
  makeValidationProps,
  provideTheme,
  useFilteredInputAttrs,
  useFilteredInputProps,
  useFocus,
  useInput,
  useProxyModel,
  useRender
} from '../../composable';
import { CdsInput, CdsInputDescription, CdsInputLabel } from '../CdsInput';

import type { IInputSlot, IInputProps, IValidationProps } from '../../composable';

export const componentName = 'CdsToggle';
export default defineComponent({
  name: componentName,
  components: { CdsTransitionSlideX },
  props: {
    ...makeInputProps(),
    ...makeValidationProps(),
    ...makeFocusProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие при фокусе
     * @type { boolean }
     */
    'update:focused': (value: boolean) => true,
    /**
     * Событие при изменении значения (v-model)
     * @type { boolean }
     */
    'update:modelValue': (value: any) => true
  },
  setup(props, { slots, attrs, expose }) {
    const model = useProxyModel(props, 'modelValue');
    const { themeClasses } = provideTheme(props);
    const { id, hasDescription } = useInput(props, slots, 'toggle');
    const { isFocused, focus, blur } = useFocus(props, componentName);
    const { inputProps } = useFilteredInputProps<IInputProps & IValidationProps & Record<string, unknown>>(props, [
      'focused'
    ]);
    const { rootAttrs, inputAttrs } = useFilteredInputAttrs(attrs);

    const inputRef = ref<HTMLInputElement>();
    const validationRef = ref<typeof CdsInput>();

    const ariaChecked = computed(() => (unref(model) ? 'true' : 'false'));

    const toggleClasses = computed(() => ({
      'cds-toggle--disabled': props.disabled,
      [classesStates.checked]: unref(model) || !!attrs.checked
    }));

    function onInput(event: Event) {
      if (props.readonly || props.disabled) return;
      model.value = (event.target as HTMLInputElement).checked;
    }

    function triggerInput() {
      if (props.readonly || props.disabled) return;
      unref(inputRef)?.click();
    }

    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        triggerInput();
      }
    };

    useRender(() => {
      const hasPrependSlot = !!slots.prepend;
      const labelSlot = slots.label || slots.append;
      const hasLabel = !!props.label || !!labelSlot;

      return h(
        CdsInput,
        {
          id: unref(id),
          ref: validationRef,
          tabindex: props.disabled ? -1 : 0,
          focused: unref(isFocused),
          class: classNames('cds-toggle', unref(toggleClasses), unref(themeClasses)),
          ...unref(inputProps),
          ...unref(rootAttrs),
          onKeydown
        },
        {
          default: (slotProps: IInputSlot) =>
            h('span', { class: 'cds-toggle__wrapper' }, [
              unref(hasDescription) &&
                h(
                  CdsInputDescription,
                  {
                    for: unref(id)
                  },
                  () => [slots.description ? slots.description() : props.description]
                ),
              h(
                'span',
                {
                  class: 'cds-toggle__control'
                },
                [
                  hasPrependSlot &&
                    h(
                      CdsInputLabel,
                      {
                        for: unref(id),
                        class: classNames('cds-toggle__label', 'cds-toggle__prepend')
                      },
                      () => slots.prepend?.()
                    ),
                  h('input', {
                    id: unref(id),
                    type: 'checkbox',
                    class: 'cds-toggle__input',
                    value: props.value,
                    ref: inputRef,
                    checked: unref(model),
                    disabled: props.disabled || props.readonly || unref(slotProps.isDisabled),
                    tabindex: props.disabled ? -1 : 0,
                    'aria-checked': unref(ariaChecked),
                    'aria-invalid': props.error || unref(slotProps.isValid) === false,
                    'aria-disabled': props.disabled || props.readonly || unref(slotProps.isDisabled),
                    ...unref(inputAttrs),
                    onBlur: blur,
                    onFocus: focus,
                    onInput: onInput
                  }),
                  h('span', {
                    class: classNames('cds-toggle__switch'),
                    onClick: triggerInput
                  }),
                  hasLabel &&
                    h(CdsInputLabel, { for: unref(id), class: 'cds-toggle__label' }, () =>
                      labelSlot ? labelSlot() : props.label
                    )
                ]
              )
            ]),
          messages: () => slots.messages?.()
        }
      );
    });

    expose({
      id,
      isFocused,
      validationRef,
      inputRef
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsToggle';
</style>
